import { isDefined } from './objectUtils';

export const toPrecision = (num: number, precision = 12): number => {
  return parseFloat(num.toPrecision(precision));
};

/**
 * Calculates the percentage of a number
 * @param of Number to get percentage of
 * @param by Percentage to get
 * @example
 * getPercent(1024, 10) // -> 102.4
 */
export const getPercent = (of: number, by: number): number => {
  return parseFloat(toPrecision(toPrecision(of) * toPrecision(by / 100)).toPrecision(2));
};

/**
 * Returns the percentage of a number
 * @param num1
 * @param num2
 * @example
 * getPercentOf(500, 1000) // -> 50
 */
export const getPercentOf = (num1: number, num2: number): number => {
  return parseFloat(toPrecision((toPrecision(num1) / toPrecision(num2)) * 100).toPrecision(2));
};

/**
 * Returns the exact percentage of a number
 * @param value
 * @param total
 * @example
 * getExactPercentOf(33.3, 100) // -> 33.3
 */
export const getExactPercentOf = (value: number, total: number): number => {
  if (total === 0) return 0;
  return (value / total) * 100;
};

/**
 * Returns the sum of a field in a list of objects
 * @param list List of objects
 * @param field which field to sum
 * @example
 * sumOfFieldInList([{price: 1300}, {price: 221}], 'price') // -> 1521
 */
export const sumOfFieldInList = <T>(list: Array<T>, field: keyof T): number => {
  return list.reduce((previousValue, currentValue) => previousValue + (currentValue[field] as number), 0);
};

/**
 * Returns the changes of a last price and close price with the percentage
 * @param lastPrice Last price
 * @param closePrice Close price
 */
export const getChangeWithPercent = (
  lastPrice: number,
  closePrice: number,
): { change: number; changePercent: number } => {
  const change = lastPrice - closePrice;
  const changePercent = change / closePrice;
  return { change, changePercent };
};

export const shadeColor = (color: string, percent: number) => {
  let R = Number(parseInt(color.substring(1, 3), 16));
  let G = Number(parseInt(color.substring(3, 5), 16));
  let B = Number(parseInt(color.substring(5, 7), 16));

  R = (R * (100 + percent)) / 100;
  G = (G * (100 + percent)) / 100;
  B = (B * (100 + percent)) / 100;

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  R = Math.round(R);
  G = Math.round(G);
  B = Math.round(B);

  const RR = R.toString(16).length === 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length === 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length === 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

const shiftNumberAsString = (number: number, shift: number) => {
  const numberAsString = number.toString();
  const shiftedString = numberAsString.replace('.', '') + '0'.repeat(shift);
  return parseFloat(shiftedString);
};
/**
 * The % operator works well with integers, but can sometimes yield unexpected results with floating point numbers due to precision issues.
 * returns the remainder of a floating point division
 * @param dividend The number to be divided.
 * @param divisor divisor
 * @example
 * floatModulo(1.0, 0.05) // -> 0.0
 */
export const floatModulo = (dividend: number, divisor: number) => {
  // Shift the dividend and the divisor to make them an integer
  const dividendDecimals = countDecimals(dividend);
  const divisorDecimals = countDecimals(divisor);

  const maxDecimals = Math.max(dividendDecimals, divisorDecimals);
  if (maxDecimals === 0) {
    return dividend % divisor;
  }

  let shiftedDividend = shiftNumberAsString(dividend, maxDecimals - dividendDecimals);
  let shiftedDivisor = shiftNumberAsString(divisor, maxDecimals - divisorDecimals);

  return shiftedDividend % shiftedDivisor;
};

/**
 * Rounds `value` to the nearest multiple of `tickSize`, using integer-domain arithmetic to avoid float precision issues.
 * @param value The number to snap.
 * @param tickSize The tick size to snap to (must be > 0).
 * @returns The nearest tick-aligned value. Returns `value` unchanged if `tickSize <= 0`.
 * @example
 * snapToNearestTick(1.03, 0.05) // -> 1.05
 * snapToNearestTick(1.02, 0.05) // -> 1.00
 */
export const snapToNearestTick = (value: number, tickSize: number): number => {
  if (tickSize <= 0) {
    return value;
  }

  const valueDecimals = countDecimals(value);
  const tickDecimals = countDecimals(tickSize);
  const maxDecimals = Math.max(valueDecimals, tickDecimals);

  if (maxDecimals === 0) {
    return Math.round(value / tickSize) * tickSize;
  }

  const multiplier = Math.pow(10, maxDecimals);
  const shiftedValue = shiftNumberAsString(value, maxDecimals - valueDecimals);
  const shiftedTick = shiftNumberAsString(tickSize, maxDecimals - tickDecimals);

  return (Math.round(shiftedValue / shiftedTick) * shiftedTick) / multiplier;
};

/**
 * Checks if the given value is defined and not NaN
 * @param value
 */
export const isValid = (value: number | undefined | null) => isDefined(value) && !isNaN(value);

/**
 * Checks if the given value is a number
 * @param value
 */
export const isNumber = (value: any): value is number => {
  return typeof value === 'number';
};

/**
 * Checks if the given value is a valid number (not NaN and not undefined and not null)
 * @param value
 */
export const isValidNumber = (value: any): value is number => {
  return isNumber(value) && isValid(value);
};

/**
 *
 * @param value
 * @returns number of decimal places
 * @example
 * countDecimals(1.123) // -> 3
 */
export const countDecimals = (value: number) => {
  if (Number.isNaN(value)) return 0;
  // Handle integers, including scientific notations (default for large numbers in js, like '1e13')
  if (Number.isInteger(value)) return 0;

  const valueAsString = value.toString();

  // Handle decimal scientific notations (like 1e-7)
  if (valueAsString.includes('e')) {
    return Math.abs(parseInt(value.toString().split('e')[1])) || 0;
  }

  if (Math.floor(value) === value) return 0;
  return value.toString().split('.')[1].length || 0;
};

/**
 *
 * @param value
 * @returns number of integer part
 * @example
 * countIntegerPart(123.12) // -> 3
 */
export const countIntegerPart = (value: number) => {
  return Math.floor(value).toString().length;
};

/**
 *
 * @param values
 * @returns the longest integer part count in an array of numbers
 * @example
 * getLongestIntegerPart([1.123, 2.1234, 3.12345]) // -> 1
 * getLongestIntegerPart([1.123, 2.1234, 3.12345, 123456]) // -> 6
 */
export const getLongestIntegerPart = (values: Array<number>) => {
  return values.reduce((currIntegerPartCount, curr) => {
    const ci = countIntegerPart(curr);
    if (ci > currIntegerPartCount) {
      return ci;
    } else {
      return currIntegerPartCount;
    }
  }, 0);
};

/**
 * @param values
 * @returns the longest decimal count in an array of numbers
 * @example
 * getLongestDecimal([1.123, 2.1234, 3.12345]) // -> 5
 */
export const getLongestDecimal = (values: Array<number>) => {
  return values.reduce((currDecimalCount, curr) => {
    const cd = countDecimals(curr);
    if (cd > currDecimalCount) {
      return cd;
    } else {
      return currDecimalCount;
    }
  }, 0);
};

export const parseFloatLocale = (value: string): number => {
  return parseFloat(value.replace(',', '.')); // replace comma with dot
};

export const localeDelimiter = parseFloat('1.1').toLocaleString('hu-HU').charAt(1);
// Replace '.' with ',' for locale-specific formatting
export const ensureLocaleDelimiter = (text: string) => text.replace('.', localeDelimiter);
